/**
 * "What's new" pop-up: the top three items of the newest changelog entry,
 * shown once per entry to Super Admin, Admin and Warehouse Coordinator.
 * Station roles never see it. "Seen" is a per-user, per-browser convenience
 * in localStorage: if storage is unavailable the pop-up simply stays away.
 */
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { PrintRunLogo } from '@/components/brand/PrintRunLogo';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { renderInline } from '@/features/organization/components/agent-api/SimpleMarkdown';
import { loadChangelog } from './changelog';
import { formatChangelogDate } from './formatDate';
import { ReleaseBanner } from './ReleaseBanner';

const WHATS_NEW_ROLES: ReadonlySet<string> = new Set(['Super Admin', 'Admin', 'Warehouse Coordinator']);
const HIGHLIGHTS = 3;
const storageKey = (userId: string) => `printrun.whatsNew.seen.${userId}`;

function readSeen(userId: string): string | null | undefined {
  try {
    return window.localStorage.getItem(storageKey(userId));
  } catch {
    return undefined; // storage blocked: treat as unknown, never nag
  }
}

function writeSeen(userId: string, date: string) {
  try {
    window.localStorage.setItem(storageKey(userId), date);
  } catch {
    /* storage blocked: nothing to remember */
  }
}

export function WhatsNewDialog() {
  const { t } = useTranslation('help');
  const { profile } = useAuth();
  const { language } = useLanguage();
  const lang = language === 'zh' ? 'zh' : 'en';
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const latest = useMemo(() => loadChangelog(lang)[0] ?? null, [lang]);
  const eligible = Boolean(profile?.id && WHATS_NEW_ROLES.has(profile.role));
  const [dismissedDate, setDismissedDate] = useState<string | null>(null);

  if (!eligible || !latest || !profile || pathname.startsWith('/help')) return null;
  const seen = readSeen(profile.id);
  const open = seen !== undefined && (seen === null || seen < latest.date) && dismissedDate !== latest.date;
  if (!open) return null;

  const close = () => {
    writeSeen(profile.id, latest.date);
    setDismissedDate(latest.date);
  };

  return (
    <Dialog open onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-lg">
        {/* Brand row: centred on the close button's line (p-6 less 10px puts a
            20px row on its 24px midline) and clear of it, so the banner below
            no longer runs under the X. */}
        <div className="-mt-2.5 flex h-5 items-center pr-8">
          <PrintRunLogo alt={t('whatsNew.logoAlt')} className="h-5" />
        </div>
        <ReleaseBanner image={latest.image} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
            {t('whatsNew.title')}
          </DialogTitle>
          <DialogDescription>
            {formatChangelogDate(latest.date, lang)} · {latest.title}
          </DialogDescription>
        </DialogHeader>
        <ol className="space-y-3">
          {latest.items.slice(0, HIGHLIGHTS).map((item, i) => (
            <li key={i} className="flex gap-3 text-sm leading-relaxed">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <p className="text-muted-foreground [&_strong]:text-foreground">{renderInline(item.text)}</p>
            </li>
          ))}
        </ol>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t('whatsNew.dismiss')}
          </Button>
          <Button
            onClick={() => {
              close();
              navigate('/help?view=changelog');
            }}
          >
            {t('whatsNew.seeAll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
